import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Download, FileText, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { CONTENT_ATTACHMENTS_BUCKET, formatBytes } from '@/lib/contentAttachments';

interface Props {
  contentId: string;
  description?: string | null;
}

const hasDescFallback = (d?: string | null) => !!d?.trim();

/** Top buttons that open a window with the content description and attachments. */
export function ContentExtrasBar({ contentId, description }: Props) {
  const [panel, setPanel] = useState<'desc' | 'files' | null>(null);
  const tab = panel ?? (hasDescFallback(description) ? 'desc' : 'files');
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data: files = [] } = useQuery({
    queryKey: ['content-attachments', contentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_bank_attachments')
        .select('id, file_name, display_name, storage_path, size_bytes')
        .eq('content_id', contentId)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
  });

  const hasDesc = !!description?.trim();
  if (!hasDesc && files.length === 0) return null;

  const download = async (f: (typeof files)[number]) => {
    setDownloading(f.id);
    try {
      const ext = f.file_name.includes('.') ? f.file_name.slice(f.file_name.lastIndexOf('.')) : '';
      const name = f.display_name.endsWith(ext) ? f.display_name : `${f.display_name}${ext}`;
      const { data, error } = await supabase.storage
        .from(CONTENT_ATTACHMENTS_BUCKET)
        .createSignedUrl(f.storage_path, 60, { download: name });
      if (error || !data) throw error;
      const res = await fetch(data.signedUrl);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      toast.error('No se pudo descargar el archivo');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-4 py-2 flex-shrink-0">
      {hasDesc && (
        <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => setPanel('desc')}>
          <FileText className="w-4 h-4 mr-1" /> Descripción
        </Button>
      )}
      {files.length > 0 && (
        <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => setPanel('files')}>
          <Paperclip className="w-4 h-4 mr-1" /> Adjuntos ({files.length})
        </Button>
      )}
      <Dialog open={panel !== null} onOpenChange={(o) => { if (!o) setPanel(null); }}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Información del contenido</DialogTitle>
            <DialogDescription>Descripción y archivos para descargar.</DialogDescription>
          </DialogHeader>
          <Tabs value={tab} onValueChange={(v) => setPanel(v as 'desc' | 'files')} className="flex-1 min-h-0 flex flex-col">
            <TabsList className="w-full">
              {hasDesc && <TabsTrigger value="desc" className="flex-1"><FileText className="w-4 h-4 mr-1" />Descripción</TabsTrigger>}
              {files.length > 0 && <TabsTrigger value="files" className="flex-1"><Paperclip className="w-4 h-4 mr-1" />Adjuntos ({files.length})</TabsTrigger>}
            </TabsList>
            {hasDesc && (
              <TabsContent value="desc" className="flex-1 min-h-0 overflow-y-auto">
                <p className="text-sm leading-relaxed text-foreground whitespace-pre-line">{description}</p>
              </TabsContent>
            )}
            {files.length > 0 && (
              <TabsContent value="files" className="flex-1 min-h-0 overflow-y-auto">
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
                      <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Paperclip className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{f.display_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {f.file_name.split('.').pop()?.toUpperCase()} {formatBytes(f.size_bytes) && `· ${formatBytes(f.size_bytes)}`}
                        </p>
                      </div>
                      <Button size="sm" disabled={downloading === f.id} onClick={() => download(f)}>
                        {downloading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 sm:mr-1" />}
                        <span className="hidden sm:inline">Descargar</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
